import {scaleNumber} from "./scaling.js";

function trimTrailingZeros(value) {
    return value.replace(/\.0+$/, "").replace(/(\.\d*[1-9])0+$/, "$1");
}

function formatScaledWithUnit(value, suffix, fixed = 1) {
    const scaled = scaleNumber(Math.abs(value), fixed);
    const match = /^([0-9.]+)([KMGT]?)$/.exec(scaled);
    if (!match) {
        return `${scaled} ${suffix}`;
    }
    const [, amount, prefix] = match;
    const unit = `${prefix}${suffix}`;
    return `${trimTrailingZeros(amount)} ${unit}`;
}

export function formatTimeTick(value) {
    return new Date(parseInt(value, 10) * 1000).toLocaleString();
}

export function formatCountAxis(value) {
    const n = Math.abs(Number(value) || 0);
    if (n < 1000) {
        return Math.round(n).toLocaleString();
    }
    return formatScaledWithUnit(n, "", n < 10000 ? 1 : 0).trim();
}

export function formatBitsPerSecondAxis(value) {
    return formatScaledWithUnit(value, "bps");
}

export function throughputUnitToBitsPerSecondMultiplier(unitCode) {
    // Mirrors `insight_types::ThroughputUnit` (serialized as a small integer).
    //
    // 0 = MiB/s (time_by_* continuous aggregates divide by 1024^2)
    // 1 = Mbps (decimal megabits per second)
    // 2 = bps  (bits per second)
    const code = Number(unitCode);
    switch (code) {
        case 1:
            return 1_000_000;
        case 2:
            return 1;
        case 0:
        default:
            return 8 * 1024 * 1024;
    }
}

export function throughputUnitToBytesPerSecondMultiplier(unitCode) {
    return throughputUnitToBitsPerSecondMultiplier(unitCode) / 8;
}

export function formatPacketsPerSecondAxis(value) {
    return formatScaledWithUnit(value, "pps");
}

export function formatPercentFromHundredthsAxis(value, digits = 1) {
    return `${trimTrailingZeros((Math.abs(Number(value) || 0) / 100).toFixed(digits))}%`;
}

export function formatPercentAxis(value, digits = 1) {
    return `${trimTrailingZeros(Math.abs(Number(value) || 0).toFixed(digits))}%`;
}

export function formatMillisecondsAxis(value, digits = 1) {
    return `${trimTrailingZeros(Math.abs(Number(value) || 0).toFixed(digits))} ms`;
}

export function createBoundedAxisTooltip({
    formatter,
    yFormatter,
    xFormatter = formatTimeTick,
    axisPointerType = "cross",
    linkAllXAxes = false,
}) {
    return {
        trigger: "axis",
        confine: true,
        axisPointer: {
            type: axisPointerType,
            ...(linkAllXAxes ? { link: [{ xAxisIndex: "all" }] } : {}),
            label: {
                backgroundColor: "rgba(30,30,30,0.92)",
                color: "#fff",
                fontFamily: "inherit",
                formatter: (params) => {
                    if (params.axisDimension === "x") {
                        return xFormatter(params.value);
                    }
                    return yFormatter(params.value);
                },
            },
        },
        backgroundColor: "rgba(30,30,30,0.92)",
        textStyle: {
            fontSize: 10,
            color: "#fff",
            fontFamily: "inherit",
        },
        formatter,
    };
}

export function applySplitDirectionAxes(option, {
    ticks,
    topLabel,
    bottomLabel,
    yFormatter,
    left = 72,
    right = 8,
    top = 18,
    bottom = 34,
}) {
    option.grid = {
        left,
        right,
        top,
        bottom,
        containLabel: false,
    };
    option.xAxis = [
        {
            type: "category",
            data: ticks,
            axisLabel: {
                formatter: formatTimeTick,
                hideOverlap: true,
                margin: 8,
                color: "rgba(210, 220, 235, 0.78)",
            },
        },
        {
            type: "category",
            data: ticks,
            axisLabel: { show: false },
            axisTick: { show: false },
            axisLine: { show: false },
        },
    ];
    option.yAxis = {
        type: "value",
        axisLabel: {
            formatter: (value) => yFormatter(Math.abs(value)),
            margin: 8,
            hideOverlap: true,
            color: "rgba(210, 220, 235, 0.82)",
        },
        splitNumber: 6,
    };
}

function collectAbsoluteExtents(series = []) {
    let maxAbs = 0;
    const considerNumeric = (value) => {
        const numeric = Number(value);
        if (Number.isFinite(numeric)) {
            maxAbs = Math.max(maxAbs, Math.abs(numeric));
        }
    };
    const stackedTotals = new Map();

    series.forEach((entry) => {
        const values = Array.isArray(entry?.data) ? entry.data : [];
        const stackKey = entry?.stack ? `${entry.stack}:${entry?.yAxisIndex ?? 0}` : null;

        values.forEach((value, index) => {
            considerNumeric(value);
            if (!stackKey) {
                return;
            }
            const numeric = Number(value);
            if (!Number.isFinite(numeric)) {
                return;
            }
            const bucketKey = `${stackKey}:${index}`;
            stackedTotals.set(bucketKey, (stackedTotals.get(bucketKey) || 0) + numeric);
        });

        const markLineData = entry?.markLine?.data;
        if (Array.isArray(markLineData)) {
            markLineData.forEach((marker) => {
                if (Array.isArray(marker)) {
                    marker.forEach((point) => considerNumeric(point?.yAxis));
                    return;
                }
                considerNumeric(marker?.yAxis);
            });
        }
    });

    stackedTotals.forEach(considerNumeric);
    return maxAbs;
}

export function roundAxisMagnitude(value) {
    if (!Number.isFinite(value) || value <= 0) {
        return 1;
    }
    const exponent = Math.floor(Math.log10(value));
    const scale = 10 ** exponent;
    const normalized = value / scale;
    let rounded = 10;
    if (normalized <= 1) {
        rounded = 1;
    } else if (normalized <= 1.5) {
        rounded = 1.5;
    } else if (normalized <= 2) {
        rounded = 2;
    } else if (normalized <= 2.5) {
        rounded = 2.5;
    } else if (normalized <= 3) {
        rounded = 3;
    } else if (normalized <= 4) {
        rounded = 4;
    } else if (normalized <= 5) {
        rounded = 5;
    }
    return rounded * scale;
}

export function setMirroredYAxisRange(option, headroom = 0.12) {
    const yAxis = Array.isArray(option.yAxis) ? option.yAxis[0] : option.yAxis;
    if (!yAxis) {
        return;
    }
    const rawMax = collectAbsoluteExtents(option.series);
    const paddedMax = rawMax > 0 ? rawMax * (1 + headroom) : 1;
    const magnitude = roundAxisMagnitude(paddedMax);
    yAxis.min = 0 - magnitude;
    yAxis.max = magnitude;
    yAxis.interval = magnitude / 5;
}

export function finalizeSplitDirectionChart(option, {
    topLabel = "Download",
    bottomLabel = "Upload",
    dividerColor = "rgba(214, 226, 255, 0.95)",
    labelColor = "rgba(223, 232, 245, 0.82)",
} = {}) {
    const grid = Array.isArray(option.grid) ? (option.grid[0] || {}) : (option.grid || {});
    const left = typeof grid.left === "number" ? grid.left : 92;
    const top = typeof grid.top === "number" ? grid.top : 18;
    const bottom = typeof grid.bottom === "number" ? grid.bottom : 34;

    const baseSeries = Array.isArray(option.series) ? option.series.filter((series) => series?.name !== "__direction-divider__") : [];
    option.series = baseSeries;
    setMirroredYAxisRange(option);

    const anchorSeries = option.series?.[0];
    if (anchorSeries) {
        const existingMarkLine = anchorSeries.markLine || {};
        const existingData = Array.isArray(existingMarkLine.data)
            ? existingMarkLine.data.filter((entry) => entry?.name !== "__direction-divider__")
            : [];
        anchorSeries.markLine = {
            ...existingMarkLine,
            silent: true,
            symbol: ["none", "none"],
            animation: false,
            label: existingMarkLine.label || { show: false },
            data: [
                ...existingData,
                {
                    name: "__direction-divider__",
                    yAxis: 0,
                    symbol: ["none", "none"],
                    lineStyle: {
                        color: dividerColor,
                        width: 2.5,
                        opacity: 1,
                    },
                    label: { show: false },
                },
            ],
        };
    }

    option.graphic = [
        {
            type: "text",
            left: left + 116,
            top: top + 14,
            silent: true,
            z: 20,
            style: {
                text: topLabel,
                fill: labelColor,
                font: "600 11px InterVar, sans-serif",
                textAlign: "left",
            },
        },
        {
            type: "text",
            left: left + 116,
            bottom: bottom + 16,
            silent: true,
            z: 20,
            style: {
                text: bottomLabel,
                fill: labelColor,
                font: "600 11px InterVar, sans-serif",
                textAlign: "left",
            },
        },
    ];
}

export function createDirectionHint(text = "Top: Download. Bottom: Upload.") {
    const hint = document.createElement("span");
    hint.classList.add("small", "text-secondary", "lqos-direction-hint");
    hint.textContent = text;
    return hint;
}

export function attachDirectionHint(base, text = "Top: Download. Bottom: Upload.") {
    return null;
}
